import { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { Download, X, Share, Smartphone } from "lucide-react";

const DISMISS_KEY = "promptkiste:install-dismissed";

const isIOS = () => {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  const isiOSDevice = /iPhone|iPad|iPod/.test(ua);
  // iPadOS 13+ reports MacIntel + touch
  const isModernIPad =
    ua.includes("Mac") && navigator.maxTouchPoints && navigator.maxTouchPoints > 1;
  return isiOSDevice || isModernIPad;
};

const isStandalone = () => {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
};

export const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone()) return; // already installed
    if (localStorage.getItem(DISMISS_KEY) === "true") return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setVisible(true);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // For iOS Safari (no beforeinstallprompt) — show a tip after a small delay
    if (isIOS() && !isStandalone()) {
      const t = setTimeout(() => {
        setIosHint(true);
        setVisible(true);
      }, 6000);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      };
    }

    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      localStorage.setItem(DISMISS_KEY, "true");
    }
    setDeferredPrompt(null);
    setVisible(false);
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, "true");
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="install-prompt"
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-sm z-[100] rounded-2xl border border-[#CCFF00]/30 bg-[#0F1014]/95 backdrop-blur-xl p-4 shadow-[0_12px_48px_-8px_rgba(0,0,0,0.6)] flex items-start gap-3 fade-up"
    >
      <span className="flex-shrink-0 w-10 h-10 rounded-xl bg-[#CCFF00] flex items-center justify-center">
        <Smartphone className="w-5 h-5 text-black" strokeWidth={2.4} />
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-white">
          App auf dem Homescreen
        </p>
        {iosHint ? (
          <p className="text-xs text-zinc-400 mt-1 leading-relaxed">
            Tippe auf <Share className="inline w-3.5 h-3.5 mx-1 -mt-0.5" /> und dann auf{" "}
            <span className="text-white font-medium">„Zum Home-Bildschirm"</span>, um PromptKiste AI wie eine App zu nutzen.
          </p>
        ) : (
          <p className="text-xs text-zinc-400 mt-1">
            Installiere PromptKiste AI für schnellen Zugriff & Fullscreen-Modus.
          </p>
        )}
        {!iosHint && (
          <Button
            data-testid="install-prompt-cta"
            onClick={handleInstall}
            className="mt-3 bg-[#CCFF00] hover:bg-[#B3E600] text-black font-semibold rounded-lg h-9 px-3 text-xs"
          >
            <Download className="w-3.5 h-3.5 mr-1.5" strokeWidth={2.4} />
            Jetzt installieren
          </Button>
        )}
      </div>
      <button
        data-testid="install-prompt-dismiss"
        onClick={handleDismiss}
        className="flex-shrink-0 -mt-1 -mr-1 p-1.5 rounded-lg text-zinc-500 hover:text-white hover:bg-white/5 transition-colors"
        aria-label="Schließen"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};
